<template>
    <section ref="profileSearch" class="flex flex-col" :aria-label="t('connections.publicProfileSearch.heading')" data-testid="link-profile-search">
        <div class="flex flex-col rounded-lgr border border-border bg-surface-raised">
            <div class="flex flex-col gap-1 border-b border-border px-6 py-6">
                <h2 class="body-serif text-ink">{{ t("connections.publicProfileSearch.introTitle", { name: requestFirstName }) }}</h2>
                <p class="body-small text-ink-muted">{{ t("connections.publicProfileSearch.introBody", { name: requestFirstName }) }}</p>
            </div>
            <div class="flex flex-col gap-3 px-6 pt-4 pb-6">
                <h3 class="body-small-semibold mb-1 text-ink-muted">{{ findHeading }}</h3>
                <BaseInput
                    v-model="searchQuery"
                    type="search"
                    :aria-label="t('connections.publicProfileSearch.searchLabel')"
                    :placeholder="t('connections.publicProfileSearch.searchLabel')"
                    :disabled="isLinking">
                    <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                </BaseInput>

                <BaseNotification v-if="searchError" theme="error" :text="t('connections.publicProfileSearch.searchFailed')" hide-dismiss />
                <BaseNotification v-if="showLinkProfileError" theme="error" :text="t('connections.publicProfileSearch.linkFailed')" @dismiss="linkProfileError = null" />

                <p class="sr-only" role="status">{{ resultStatus }}</p>

                <div v-if="isSearching" class="flex items-center justify-center gap-2 py-10 text-ink-muted">
                    <LoadingSpinner class="size-4" aria-hidden="true" />
                    <span class="body-small">{{ t("connections.publicProfileSearch.searching") }}</span>
                </div>

                <template v-else-if="profiles.length > 0">
                    <ul ref="resultsList" class="divide-y divide-border max-h-80 overflow-auto" :aria-label="t('connections.publicProfileSearch.resultsLabel')">
                        <li v-for="profile in profiles" :key="profile.id" class="group flex items-center gap-3 py-2 pl-2">
                            <CollaborationProfileAvatar size="sm" :name="profile.name" />
                            <span class="flex min-w-0 grow flex-col">
                                <span class="body-small-semibold truncate">{{ profile.name }}</span>
                                <span v-if="subtitleFor(profile)" class="body-small text-ink-muted truncate">{{ subtitleFor(profile) }}</span>
                            </span>
                            <BaseButton
                                theme="primary"
                                size="sm"
                                class="flex min-w-18 items-center justify-center gap-2 opacity-0 transition-opacity duration-(--motion-duration-fast) group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"
                                :class="{ 'opacity-100': isLinking && linkingProfileUserId === profile.user }"
                                :disabled="isLinking"
                                :aria-disabled="isLinking"
                                :aria-label="
                                    t('connections.publicProfileSearch.linkProfileLabel', {
                                        link: t('connections.publicProfileSearch.link'),
                                        name: profile.name ?? t('connections.publicProfileSearch.profileFallbackName'),
                                    })
                                "
                                @click="linkSelectedProfile(profile)">
                                <span>{{ t("connections.publicProfileSearch.link") }}</span>
                                <LoadingSpinner v-if="isLinking && linkingProfileUserId === profile.user" class="size-3.5" aria-hidden="true" />
                            </BaseButton>
                        </li>
                        <li v-if="isLoadingMore" class="flex items-center justify-center gap-2 py-3 text-ink-muted">
                            <LoadingSpinner class="size-4" aria-hidden="true" />
                            <span class="body-small">{{ t("connections.publicProfileSearch.loadingMore") }}</span>
                        </li>
                    </ul>
                </template>

                <p v-else-if="hasNoMatch && searchQuery.trim()" class="body-small text-ink-muted" data-testid="link-profile-no-match">
                    {{ t("connections.publicProfileSearch.noMatchQuery", { query: searchQuery.trim() }) }}
                </p>
            </div>

            <div class="flex flex-col gap-3 border-t border-border px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex flex-col">
                    <h3 class="body-small-semibold text-ink-muted">
                        {{
                            profiles.length > 0
                                ? t("connections.publicProfileSearch.notRightTitle", { name: requestFirstName })
                                : t("connections.publicProfileSearch.requestTitle", { name: requestFirstName })
                        }}
                    </h3>
                    <p class="body-small text-ink-muted">{{ t("connections.publicProfileSearch.requestBody", { name: requestFirstName }) }}</p>
                </div>
                <PublicProfileRequestLinkAction
                    :copied="isCopied"
                    hide-dismiss
                    :disabled="isLinking || isCopying"
                    theme="neutral"
                    size="sm"
                    class="shrink-0"
                    @copy="copyRequestLink"
                    @dismiss="dismissSearch" />
            </div>
        </div>
        <BaseNotification v-if="requestCopyFailed" class="mt-4" theme="error" :text="t('connections.publicProfileSearch.copyFailed')" @dismiss="requestCopyFailed = false" />
    </section>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useInfiniteScroll } from "@vueuse/core";
import { copyShareUrl, profileInviteMessage } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { logProfileShared, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import { useLinkPublicProfile } from "~vue/composables/useLinkPublicProfile";
import { usePublicProfileSearch } from "~vue/composables/usePublicProfileSearch";
import { useTimedShow } from "~vue/composables/useTimedShow";
import PublicProfileRequestLinkAction from "~vue/Connections/PublicProfileRequestLinkAction.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { ConnectionDict } from "~vue/types/connection";
import type { ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getFirstName } from "~vue/utils/stringUtils";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const {
    connection,
    inviteUrl,
    autoFocus = false,
} = defineProps<{
    connection: ConnectionDict;
    inviteUrl: string;
    autoFocus?: boolean;
}>();

const emit = defineEmits<{ dismiss: [] }>();
const { t } = useI18n();

const profileSearch = useTemplateRef<HTMLElement>("profileSearch");
const initialSearchTerm = connection.persona.name ?? "";
const {
    query: searchQuery,
    profiles,
    error: searchError,
    isFetching: isSearching,
    hasNoMatch,
    total: resultTotal,
    hasMore,
    isLoadingMore,
    loadMore,
} = usePublicProfileSearch({
    initialSearchTerm,
    source: "my_people",
});

// When the person's own name finds nobody, start from an empty search rather than showing a dead end.
const stopClearingUnmatchedName = watch([hasNoMatch, () => profiles.value.length], ([noMatch, found]) => {
    if (!noMatch && found === 0) {
        return;
    }
    if (noMatch && searchQuery.value === initialSearchTerm) {
        searchQuery.value = "";
    }
    stopClearingUnmatchedName();
});

const requestFirstName = computed(() => getFirstName(initialSearchTerm) || t("connections.publicProfileSearch.thisPerson"));
const findHeading = computed(() =>
    !isSearching.value && profiles.value.length > 0
        ? t("connections.publicProfileSearch.foundCount", { count: Math.max(resultTotal.value, profiles.value.length) })
        : t("connections.publicProfileSearch.findTitle", { name: requestFirstName.value }),
);

const resultsList = useTemplateRef<HTMLElement>("resultsList");
useInfiniteScroll(resultsList, loadMore, { distance: 80, canLoadMore: () => hasMore.value });

const subtitleFor = (profile: ExistingPublicProfile) => [profile.title, profile.division, profile.email].filter(Boolean).join(" · ");
const resultStatus = computed(() => {
    if (isSearching.value) {
        return t("connections.publicProfileSearch.status.searching");
    }
    if (searchError.value) {
        return t("connections.publicProfileSearch.status.failed");
    }
    if (isLoadingMore.value) {
        return t("connections.publicProfileSearch.status.loadingMore");
    }
    if (profiles.value.length > 0) {
        return t("connections.publicProfileSearch.status.found", { count: profiles.value.length }, profiles.value.length);
    }
    if (hasNoMatch.value) {
        return t("connections.publicProfileSearch.status.noneFound");
    }
    return "";
});

const linkingProfileUserId = ref<string | null>(null);
const {
    linkProfile,
    isLinking,
    error: linkProfileError,
} = useLinkPublicProfile({
    connectionId: computed(() => connection.id),
    source: "my_people",
    onLinked: () => {
        router.reload({
            only: ["connection", "public_profile_user_id", "public_profile_basic_info", "pair_perspective_url", "pair_perspective_page_url", "person_teams_url"],
            onSuccess: async () => {
                await nextTick();
                document.querySelector<HTMLElement>("[data-testid='linked-profile-section']")?.focus();
            },
        });
    },
});

const showLinkProfileError = computed(() => !!linkProfileError.value && !isSessionEndedError(linkProfileError.value));

const linkSelectedProfile = (profile: ExistingPublicProfile) => {
    if (isLinking.value) {
        return;
    }
    linkingProfileUserId.value = profile.user;
    linkProfile(profile.user);
};

const { show: showCopiedRequest, isShowing: isCopied } = useTimedShow({ duration: 10_000 });
const hasCopiedRequest = ref(false);
const isCopying = ref(false);
const requestCopyFailed = ref(false);

const copyRequestLink = async () => {
    if (isCopying.value) {
        return;
    }
    isCopying.value = true;
    requestCopyFailed.value = false;
    try {
        if (!(await copyShareUrl(withShareAttribution(inviteUrl, "my_people", "link"), profileInviteMessage(requestFirstName.value)))) {
            requestCopyFailed.value = true;
            return;
        }
        showCopiedRequest();
        hasCopiedRequest.value = true;
        logUserInteraction(AmplitudeEventNames.CONNECTION_INVITE_COPIED, {
            connection_id: connection.id,
            source: "my_people",
        });
        void logProfileShared({ shareSource: "my_people", target: "link", shareKind: "invite", connectionId: connection.id });
    } finally {
        isCopying.value = false;
    }
};

const dismissSearch = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_LINK_SEARCH_CANCELED, {
        connection_id: connection.id,
        copied_invite: hasCopiedRequest.value,
        source: "my_people",
    });
    emit("dismiss");
};

onMounted(async () => {
    if (autoFocus) {
        await nextTick();
        profileSearch.value?.querySelector<HTMLInputElement>("input")?.focus();
    }
});
</script>
